import { useEffect, useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import StatusBadge from "@/components/StatusBadge";
import InvoiceFormDialog from "@/components/dialogs/InvoiceFormDialog";
import MeetingFormDialog from "@/components/dialogs/MeetingFormDialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api, { API, formatApiErrorDetail, fileUrl, downloadUrl } from "@/lib/api";
import { buildGoogleCalendarLink } from "@/lib/googleCalendar";
import { toast } from "sonner";
import { Plus, Download, Upload, Clock, CalendarPlus, Sparkles, Copy, Trash2, UserPlus, X, KeyRound, Send, CheckCircle2, Circle, ListChecks, Eye, Mail, ArrowDownToLine, ArrowUpFromLine, FileX2, Pencil, FileText } from "lucide-react";

function daysUntil(dateStr) {
  const d = new Date(dateStr);
  const now = new Date();
  const diff = Math.ceil((d - now) / (1000 * 60 * 60 * 24));
  return diff;
}

export default function ClientDetailSheet({ clientId, open, onOpenChange, onUpdated }) {
  const [client, setClient] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [meetings, setMeetings] = useState([]);
  const [files, setFiles] = useState([]);
  const [invoiceDialogOpen, setInvoiceDialogOpen] = useState(false);
  const [meetingDialogOpen, setMeetingDialogOpen] = useState(false);
  const [draftType, setDraftType] = useState("meeting_summary");
  const [additionalContext, setAdditionalContext] = useState("");
  const [generatedText, setGeneratedText] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);
  const [pastDrafts, setPastDrafts] = useState([]);
  const [team, setTeam] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [teamEmployeeId, setTeamEmployeeId] = useState("");
  const [teamRoleLabel, setTeamRoleLabel] = useState("");
  const [portalAccess, setPortalAccess] = useState({ enabled: false, email: null, active: true });
  const [portalEmail, setPortalEmail] = useState("");
  const [portalPassword, setPortalPassword] = useState("");
  const [comments, setComments] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [checklists, setChecklists] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [assignTemplateId, setAssignTemplateId] = useState("");

  const load = async () => {
    if (!clientId) return;
    const [c, inv, mtg, fl, drafts, tm, emp, pa, cm, cl, tpl] = await Promise.all([
      api.get(`/clients/${clientId}`),
      api.get(`/clients/${clientId}/invoices`),
      api.get(`/clients/${clientId}/meetings`),
      api.get(`/clients/${clientId}/files`),
      api.get(`/clients/${clientId}/ai-drafts`),
      api.get(`/clients/${clientId}/team`),
      api.get(`/employees`),
      api.get(`/clients/${clientId}/portal-access`),
      api.get(`/clients/${clientId}/comments`),
      api.get(`/clients/${clientId}/checklists`),
      api.get(`/templates`),
    ]);
    setClient(c.data);
    setInvoices(inv.data);
    setMeetings(mtg.data);
    setFiles(fl.data);
    setPastDrafts(drafts.data);
    setTeam(tm.data);
    setEmployees(emp.data);
    setPortalAccess(pa.data);
    setPortalEmail(pa.data.email || "");
    setComments(cm.data);
    setChecklists(cl.data);
    setTemplates(tpl.data);
  };

  useEffect(() => {
    if (open && clientId) load();
  }, [open, clientId]);

  const updateStatus = async (status) => {
    try {
      await api.put(`/clients/${clientId}`, { status });
      toast.success("Client status updated");
      load();
      onUpdated?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const deleteClient = async () => {
    try {
      await api.delete(`/clients/${clientId}`);
      toast.success("Client and related records deleted");
      onOpenChange(false);
      onUpdated?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const updateInvoiceStatus = async (invoiceId, status) => {
    try {
      await api.put(`/clients/${clientId}/invoices/${invoiceId}`, { status });
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const uploadInvoiceFile = async (invoiceId, file) => {
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/clients/${clientId}/invoices/${invoiceId}/file`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Invoice file attached");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const deleteInvoiceFile = async (invoiceId) => {
    try {
      await api.delete(`/clients/${clientId}/invoices/${invoiceId}/file`);
      toast.success("Invoice file removed");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const uploadClientFile = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/clients/${clientId}/files`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("File uploaded");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const replaceClientFile = async (fileId, file) => {
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.put(`/clients/${clientId}/files/${fileId}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("File replaced");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const deleteClientFile = async (fileId) => {
    try {
      await api.delete(`/clients/${clientId}/files/${fileId}`);
      toast.success("File deleted");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const emailInvoice = async (invoiceId) => {
    try {
      const res = await api.post(`/clients/${clientId}/invoices/${invoiceId}/email`);
      toast.success(res.data.message || "Invoice emailed");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const generateDraft = async () => {
    setIsGenerating(true);
    setGeneratedText("");
    try {
      const res = await fetch(`${API}/clients/${clientId}/ai-draft`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ draft_type: draftType, additional_context: additionalContext }),
      });
      if (!res.ok || !res.body) throw new Error("Failed to start generation");
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop();
        for (const part of parts) {
          if (!part.startsWith("data: ")) continue;
          const data = JSON.parse(part.slice(6));
          if (data.delta) setGeneratedText((prev) => prev + data.delta);
          if (data.error) toast.error(data.error);
          if (data.done) load();
        }
      }
    } catch (e) {
      toast.error("Could not generate draft. Please try again.");
    } finally {
      setIsGenerating(false);
    }
  };

  const copyDraft = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard");
  };

  const addTeamMember = async () => {
    if (!teamEmployeeId || !teamRoleLabel.trim()) {
      toast.error("Select an employee and enter a role");
      return;
    }
    try {
      await api.post(`/clients/${clientId}/team`, { employee_id: teamEmployeeId, role_label: teamRoleLabel.trim() });
      toast.success("Team member assigned");
      setTeamEmployeeId("");
      setTeamRoleLabel("");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const removeTeamMember = async (memberId) => {
    try {
      await api.delete(`/clients/${clientId}/team/${memberId}`);
      toast.success("Removed from team");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const savePortalAccess = async () => {
    if (!portalEmail.trim() || !portalPassword.trim()) {
      toast.error("Email and password are required");
      return;
    }
    try {
      await api.post(`/clients/${clientId}/portal-access`, { email: portalEmail.trim(), password: portalPassword.trim() });
      toast.success("Portal access saved");
      setPortalPassword("");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const togglePortalAccess = async () => {
    try {
      await api.put(`/clients/${clientId}/portal-access/toggle`);
      toast.success("Portal access updated");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const sendMessage = async () => {
    if (!newMessage.trim()) return;
    try {
      await api.post(`/clients/${clientId}/comments`, { message: newMessage.trim() });
      setNewMessage("");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const assignTemplate = async () => {
    if (!assignTemplateId) {
      toast.error("Select a template first");
      return;
    }
    try {
      await api.post(`/templates/${assignTemplateId}/assign`, { client_id: clientId });
      toast.success("Checklist assigned to client");
      setAssignTemplateId("");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const toggleChecklistItem = async (checklistId, itemId) => {
    try {
      await api.put(`/clients/${clientId}/checklists/${checklistId}/items/${itemId}`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  if (!client) return null;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-2xl overflow-y-auto" data-testid="client-detail-sheet">
        <SheetHeader>
          <div className="flex items-center justify-between pr-8">
            <SheetTitle className="font-heading" data-testid="client-sheet-name">{client.company_name}</SheetTitle>
            <div className="flex items-center gap-2">
              <Select value={client.status} onValueChange={updateStatus}>
                <SelectTrigger data-testid="client-sheet-status-select" className="w-36 h-8"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Onboarding">Onboarding</SelectItem>
                  <SelectItem value="Active">Active</SelectItem>
                  <SelectItem value="Completed">Completed</SelectItem>
                </SelectContent>
              </Select>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <button data-testid="delete-client-button" className="h-8 w-8 flex items-center justify-center rounded-md border border-[#E2E8F0] text-red-600 hover:bg-red-50 transition-colors duration-200">
                    <Trash2 className="h-4 w-4" />
                  </button>
                </AlertDialogTrigger>
                <AlertDialogContent data-testid="delete-client-confirm-dialog">
                  <AlertDialogHeader>
                    <AlertDialogTitle>Delete {client.company_name}?</AlertDialogTitle>
                    <AlertDialogDescription>
                      This permanently deletes the client along with all its invoices, meetings, files and AI drafts.
                      Tasks linked to this client will be unlinked, not deleted. This action cannot be undone.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel data-testid="delete-client-cancel-button">Cancel</AlertDialogCancel>
                    <AlertDialogAction data-testid="delete-client-confirm-button" onClick={deleteClient} className="bg-red-600 hover:bg-red-700">
                      Delete Client
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          </div>
          <p className="text-sm text-[#4B5563]">{client.engagement_scope}</p>
        </SheetHeader>

        <Tabs defaultValue="overview" className="mt-6">
          <TabsList className="flex flex-wrap w-full gap-1 h-auto">
            <TabsTrigger value="overview" data-testid="client-tab-overview">Overview</TabsTrigger>
            <TabsTrigger value="financial" data-testid="client-tab-financial">Financial Hub</TabsTrigger>
            <TabsTrigger value="engagement" data-testid="client-tab-engagement">Engagement</TabsTrigger>
            <TabsTrigger value="files" data-testid="client-tab-files">Files</TabsTrigger>
            <TabsTrigger value="team" data-testid="client-tab-team">Team</TabsTrigger>
            <TabsTrigger value="portal" data-testid="client-tab-portal">Portal Access</TabsTrigger>
            <TabsTrigger value="checklists" data-testid="client-tab-checklists">Checklists</TabsTrigger>
            <TabsTrigger value="messages" data-testid="client-tab-messages">Messages</TabsTrigger>
            <TabsTrigger value="ai" data-testid="client-tab-ai">AI Assistant</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="mt-4 grid grid-cols-2 gap-3 text-sm">
            <Field label="POC Name" value={client.poc_name} />
            <Field label="POC Phone" value={client.poc_phone} />
            <Field label="POC Email" value={client.poc_email} />
            <Field label="Contract Start" value={client.contract_start_date} />
            <Field label="Project End" value={client.project_end_date} />
          </TabsContent>

          <TabsContent value="financial" className="mt-4 space-y-3">
            <div className="flex justify-end">
              <Button size="sm" onClick={() => setInvoiceDialogOpen(true)} data-testid="add-invoice-button" className="bg-[#00823B] hover:bg-[#006A2F] text-white">
                <Plus className="h-3.5 w-3.5 mr-1" /> New Invoice
              </Button>
            </div>
            <div className="overflow-x-auto">
            <table className="w-full text-sm border border-[#E2E8F0] rounded-md overflow-hidden">
              <thead className="bg-[#F8F9FA] text-xs uppercase text-[#4B5563]">
                <tr>
                  <th className="px-3 py-2 text-left font-medium">Invoice #</th>
                  <th className="px-3 py-2 text-left font-medium">GST</th>
                  <th className="px-3 py-2 text-left font-medium">Amount</th>
                  <th className="px-3 py-2 text-left font-medium">Due</th>
                  <th className="px-3 py-2 text-left font-medium">Status</th>
                  <th className="px-3 py-2 text-left font-medium">File</th>
                  <th className="px-3 py-2 text-left font-medium">GST PDF</th>
                  <th className="px-3 py-2 text-left font-medium">Email</th>
                </tr>
              </thead>
              <tbody>
                {invoices.length === 0 && <tr><td colSpan={8} className="px-3 py-4 text-center text-[#4B5563]">No invoices yet.</td></tr>}
                {invoices.map((inv) => (
                  <tr key={inv.id} data-testid={`invoice-row-${inv.id}`} className="border-t border-[#E2E8F0]">
                    <td className="px-3 py-2 font-medium">{inv.invoice_number}</td>
                    <td className="px-3 py-2 text-xs text-[#4B5563]">{inv.gst_type && inv.gst_type !== "None" ? inv.gst_type : "—"}</td>
                    <td className="px-3 py-2">₹{Number(inv.amount).toLocaleString("en-IN")}</td>
                    <td className="px-3 py-2">{inv.due_date}</td>
                    <td className="px-3 py-2">
                      <Select value={inv.status} onValueChange={(v) => updateInvoiceStatus(inv.id, v)}>
                        <SelectTrigger data-testid={`invoice-status-select-${inv.id}`} className="h-7 w-28 text-xs"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Draft">Draft</SelectItem>
                          <SelectItem value="Sent">Sent</SelectItem>
                          <SelectItem value="Paid">Paid</SelectItem>
                          <SelectItem value="Overdue">Overdue</SelectItem>
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="px-3 py-2">
                      {inv.file_path ? (
                        <div className="flex items-center gap-2.5">
                          <a href={fileUrl(inv.file_path)} target="_blank" rel="noreferrer" data-testid={`invoice-file-view-${inv.id}`} title="View"><Eye className="h-4 w-4 text-[#4B5563]" /></a>
                          <a href={downloadUrl(inv.file_path, inv.invoice_number)} data-testid={`invoice-file-link-${inv.id}`} title="Download"><Download className="h-4 w-4 text-[#00823B]" /></a>
                          <label className="cursor-pointer text-[#4B5563] hover:text-[#1C1C1E]" data-testid={`invoice-file-replace-label-${inv.id}`} title="Replace file">
                            <Pencil className="h-4 w-4" />
                            <input type="file" className="hidden" onChange={(e) => e.target.files[0] && uploadInvoiceFile(inv.id, e.target.files[0])} />
                          </label>
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <button data-testid={`invoice-file-delete-${inv.id}`} className="text-red-600" title="Delete file"><Trash2 className="h-4 w-4" /></button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Remove this invoice file?</AlertDialogTitle>
                                <AlertDialogDescription>This action cannot be undone.</AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancel</AlertDialogCancel>
                                <AlertDialogAction onClick={() => deleteInvoiceFile(inv.id)} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                      ) : (
                        <label className="cursor-pointer text-[#4B5563]" data-testid={`invoice-file-upload-label-${inv.id}`}>
                          <Upload className="h-4 w-4" />
                          <input type="file" className="hidden" onChange={(e) => e.target.files[0] && uploadInvoiceFile(inv.id, e.target.files[0])} />
                        </label>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <a href={`${API}/clients/${clientId}/invoices/${inv.id}/pdf`} target="_blank" rel="noreferrer" data-testid={`invoice-gst-pdf-${inv.id}`} className="text-[#4B5563] hover:text-[#00823B]" title="Download GST invoice PDF">
                        <FileText className="h-4 w-4" />
                      </a>
                    </td>
                    <td className="px-3 py-2">
                      <button data-testid={`invoice-email-button-${inv.id}`} onClick={() => emailInvoice(inv.id)} className="text-[#4B5563] hover:text-[#00823B]" title="Email invoice to client">
                        <Mail className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          </TabsContent>

          <TabsContent value="engagement" className="mt-4 space-y-3">
            <div className="flex justify-end">
              <Button size="sm" onClick={() => setMeetingDialogOpen(true)} data-testid="add-meeting-button" className="bg-[#00823B] hover:bg-[#006A2F] text-white">
                <Plus className="h-3.5 w-3.5 mr-1" /> Log Meeting
              </Button>
            </div>
            <div className="space-y-2">
              {meetings.length === 0 && <p className="text-sm text-[#4B5563] text-center py-4">No meetings logged yet.</p>}
              {meetings.map((m) => {
                const d = daysUntil(m.meeting_date);
                return (
                  <div key={m.id} data-testid={`meeting-row-${m.id}`} className="p-3 border border-[#E2E8F0] rounded-md flex items-start justify-between">
                    <div>
                      <p className="text-sm font-medium text-[#1C1C1E]">{m.title}</p>
                      <p className="text-xs text-[#4B5563] mt-0.5">{new Date(m.meeting_date).toLocaleString()}</p>
                      {m.notes && <p className="text-xs text-[#4B5563] mt-1">{m.notes}</p>}
                      <a
                        href={buildGoogleCalendarLink({ title: m.title, description: m.notes, startDate: m.meeting_date, attendees: m.attendee_emails || [] })}
                        target="_blank"
                        rel="noreferrer"
                        data-testid={`meeting-add-calendar-${m.id}`}
                        className="inline-flex items-center gap-1 text-xs text-[#00823B] hover:underline mt-1.5"
                      >
                        <CalendarPlus className="h-3 w-3" /> Add to Google Calendar / Meet
                      </a>
                    </div>
                    <span className="text-xs flex items-center gap-1 text-[#00823B] font-medium whitespace-nowrap">
                      <Clock className="h-3 w-3" /> {d >= 0 ? `in ${d}d` : `${Math.abs(d)}d ago`}
                    </span>
                  </div>
                );
              })}
            </div>
          </TabsContent>

          <TabsContent value="files" className="mt-4 space-y-3">
            <label className="inline-block">
              <Button size="sm" asChild data-testid="add-client-file-button" className="bg-[#00823B] hover:bg-[#006A2F] text-white cursor-pointer">
                <span><Plus className="h-3.5 w-3.5 mr-1" /> Upload File</span>
              </Button>
              <input type="file" className="hidden" onChange={(e) => e.target.files[0] && uploadClientFile(e.target.files[0])} />
            </label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 items-start">
              <ClientFileSection
                title="Delivered by EnviroWealth"
                icon={ArrowDownToLine}
                files={files.filter((f) => f.category !== "Received from Client")}
                emptyText="No deliverables shared yet."
                onDelete={deleteClientFile}
                onReplace={replaceClientFile}
              />
              <ClientFileSection
                title="Received from Client"
                icon={ArrowUpFromLine}
                files={files.filter((f) => f.category === "Received from Client")}
                emptyText="Nothing received yet."
                onDelete={deleteClientFile}
                onReplace={replaceClientFile}
              />
            </div>
          </TabsContent>

          <TabsContent value="team" className="mt-4 space-y-3">
            <div className="flex items-end gap-2 bg-[#F8F9FA] border border-[#E2E8F0] rounded-md p-3">
              <div className="flex-1 space-y-1">
                <p className="text-xs text-[#4B5563] uppercase tracking-wide">Employee</p>
                <Select value={teamEmployeeId} onValueChange={setTeamEmployeeId}>
                  <SelectTrigger data-testid="team-employee-select" className="h-9"><SelectValue placeholder="Select employee" /></SelectTrigger>
                  <SelectContent>
                    {employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex-1 space-y-1">
                <p className="text-xs text-[#4B5563] uppercase tracking-wide">Role</p>
                <Input data-testid="team-role-input" placeholder="e.g. ESG Analyst" value={teamRoleLabel} onChange={(e) => setTeamRoleLabel(e.target.value)} className="h-9" />
              </div>
              <Button size="sm" data-testid="add-team-member-button" onClick={addTeamMember} className="bg-[#00823B] hover:bg-[#006A2F] text-white h-9">
                <UserPlus className="h-3.5 w-3.5 mr-1" /> Assign
              </Button>
            </div>
            <div className="space-y-2">
              {team.length === 0 && <p className="text-sm text-[#4B5563] text-center py-4">No team members assigned yet.</p>}
              {team.map((t) => (
                <div key={t.id} data-testid={`team-row-${t.id}`} className="flex items-center justify-between p-3 border border-[#E2E8F0] rounded-md">
                  <div>
                    <p className="text-sm font-medium text-[#1C1C1E]">{t.employee_name}</p>
                    <p className="text-xs text-[#00823B]">{t.role_label}</p>
                  </div>
                  <button data-testid={`remove-team-member-${t.id}`} onClick={() => removeTeamMember(t.id)} className="text-[#4B5563] hover:text-red-600">
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="portal" className="mt-4 space-y-3">
            <div className="bg-[#F8F9FA] border border-[#E2E8F0] rounded-md p-4 space-y-3">
              <div className="flex items-center gap-2">
                <KeyRound className="h-4 w-4 text-[#00823B]" />
                <p className="text-sm font-semibold text-[#1C1C1E]">Client Login Credentials</p>
                {portalAccess.enabled && (
                  <span className={`ml-auto text-xs font-medium px-2 py-0.5 rounded-md ${portalAccess.active ? "bg-[#76C121]/10 text-[#00823B]" : "bg-red-50 text-red-600"}`}>
                    {portalAccess.active ? "Active" : "Disabled"}
                  </span>
                )}
              </div>
              <div className="space-y-1">
                <p className="text-xs text-[#4B5563] uppercase tracking-wide">Login Email</p>
                <Input data-testid="portal-email-input" type="email" value={portalEmail} onChange={(e) => setPortalEmail(e.target.value)} placeholder="client@company.com" />
              </div>
              <div className="space-y-1">
                <p className="text-xs text-[#4B5563] uppercase tracking-wide">{portalAccess.enabled ? "New Password (leave blank to keep current)" : "Password"}</p>
                <Input data-testid="portal-password-input" type="password" value={portalPassword} onChange={(e) => setPortalPassword(e.target.value)} placeholder="••••••••" />
              </div>
              <div className="flex gap-2">
                <Button size="sm" data-testid="save-portal-access-button" onClick={savePortalAccess} className="bg-[#00823B] hover:bg-[#006A2F] text-white">
                  {portalAccess.enabled ? "Update Credentials" : "Grant Portal Access"}
                </Button>
                {portalAccess.enabled && (
                  <Button size="sm" variant="outline" data-testid="toggle-portal-access-button" onClick={togglePortalAccess}>
                    {portalAccess.active ? "Disable Access" : "Enable Access"}
                  </Button>
                )}
              </div>
            </div>
          </TabsContent>

          <TabsContent value="checklists" className="mt-4 space-y-4">
            <div className="flex items-end gap-2 bg-[#F8F9FA] border border-[#E2E8F0] rounded-md p-3">
              <div className="flex-1 space-y-1">
                <p className="text-xs text-[#4B5563] uppercase tracking-wide">Assign a Checklist Template</p>
                <Select value={assignTemplateId} onValueChange={setAssignTemplateId}>
                  <SelectTrigger data-testid="assign-template-select" className="h-9"><SelectValue placeholder="Select template" /></SelectTrigger>
                  <SelectContent>
                    {templates.map((t) => <SelectItem key={t.id} value={t.id}>{t.name} ({t.category})</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <Button size="sm" data-testid="assign-template-button" onClick={assignTemplate} className="bg-[#00823B] hover:bg-[#006A2F] text-white h-9">
                <ListChecks className="h-3.5 w-3.5 mr-1" /> Assign
              </Button>
            </div>
            {checklists.length === 0 && <p className="text-sm text-[#4B5563] text-center py-4">No checklists assigned yet.</p>}
            {checklists.map((cl) => {
              const done = cl.items.filter((i) => i.completed).length;
              return (
                <div key={cl.id} data-testid={`checklist-block-${cl.id}`} className="border border-[#E2E8F0] rounded-md p-3">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-sm font-semibold text-[#1C1C1E]">{cl.template_name}</p>
                    <span className="text-xs text-[#4B5563]">{done}/{cl.items.length} complete</span>
                  </div>
                  <div className="space-y-1.5">
                    {cl.items.map((it) => (
                      <button
                        key={it.id}
                        data-testid={`checklist-item-${it.id}`}
                        onClick={() => toggleChecklistItem(cl.id, it.id)}
                        className="flex items-center gap-2 text-sm w-full text-left hover:bg-[#F8F9FA] rounded px-1 py-0.5"
                      >
                        {it.completed ? <CheckCircle2 className="h-4 w-4 text-[#00823B] shrink-0" /> : <Circle className="h-4 w-4 text-[#4B5563] shrink-0" />}
                        <span className={it.completed ? "text-[#4B5563] line-through" : "text-[#1C1C1E]"}>{it.text}</span>
                      </button>
                    ))}
                  </div>
                </div>
              );
            })}
          </TabsContent>

          <TabsContent value="messages" className="mt-4">
            <div className="border border-[#E2E8F0] rounded-md p-4 space-y-3 max-h-[360px] overflow-y-auto">
              {comments.length === 0 && <p className="text-sm text-[#4B5563] text-center py-4">No messages yet.</p>}
              {comments.map((c) => (
                <div key={c.id} data-testid={`comment-row-${c.id}`} className={`max-w-[80%] ${c.author_role !== "client" ? "ml-auto text-right" : ""}`}>
                  <div className={`inline-block px-3 py-2 rounded-lg text-sm ${c.author_role === "client" ? "bg-[#F8F9FA] text-[#1C1C1E]" : "bg-[#76C121]/10 text-[#1C1C1E]"}`}>
                    {c.message}
                  </div>
                  <p className="text-[10px] text-[#4B5563] mt-1">{c.author_name} ({c.author_role}) · {new Date(c.created_at).toLocaleString()}</p>
                </div>
              ))}
            </div>
            <div className="flex gap-2 mt-3">
              <Textarea data-testid="admin-message-input" placeholder="Message the client..." value={newMessage} onChange={(e) => setNewMessage(e.target.value)} rows={1} className="resize-none" />
              <Button data-testid="admin-send-message-button" onClick={sendMessage} className="bg-[#76C121] hover:bg-[#5C9819] text-white shrink-0">
                <Send className="h-4 w-4" />
              </Button>
            </div>
          </TabsContent>

          <TabsContent value="ai" className="mt-4 space-y-4">
            <div className="border border-[#E2E8F0] rounded-md p-4 space-y-3 bg-[#F7FEE7]">
              <div className="flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-[#00823B]" />
                <p className="text-sm font-semibold text-[#1C1C1E]">AI Report & Proposal Drafting Assistant</p>
              </div>
              <Select value={draftType} onValueChange={setDraftType}>
                <SelectTrigger data-testid="ai-draft-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="esg_audit_report">ESG Audit Report</SelectItem>
                  <SelectItem value="client_proposal">Client Proposal</SelectItem>
                  <SelectItem value="meeting_summary">Meeting Summary</SelectItem>
                  <SelectItem value="portal_recommendation">Recommendations for Next Year (visible to client)</SelectItem>
                </SelectContent>
              </Select>
              <Textarea
                data-testid="ai-draft-context-input"
                placeholder="Optional: add extra context or instructions for the AI..."
                value={additionalContext}
                onChange={(e) => setAdditionalContext(e.target.value)}
                rows={2}
              />
              <Button
                onClick={generateDraft}
                disabled={isGenerating}
                data-testid="ai-draft-generate-button"
                className="bg-[#00823B] hover:bg-[#006A2F] text-white"
              >
                <Sparkles className="h-3.5 w-3.5 mr-1.5" /> {isGenerating ? "Generating..." : "Generate Draft"}
              </Button>
              {(generatedText || isGenerating) && (
                <div className="bg-white border border-[#E2E8F0] rounded-md p-3 relative">
                  <p data-testid="ai-draft-output" className="text-sm text-[#1C1C1E] whitespace-pre-wrap">{generatedText}{isGenerating && <span className="animate-pulse">▍</span>}</p>
                  {generatedText && !isGenerating && (
                    <button onClick={() => copyDraft(generatedText)} data-testid="ai-draft-copy-button" className="absolute top-2 right-2 text-[#4B5563] hover:text-[#00823B]">
                      <Copy className="h-4 w-4" />
                    </button>
                  )}
                </div>
              )}
            </div>
            <div className="space-y-2">
              <p className="text-xs text-[#4B5563] uppercase tracking-wide">Past Drafts</p>
              {pastDrafts.length === 0 && <p className="text-sm text-[#4B5563] text-center py-4">No drafts generated yet.</p>}
              {pastDrafts.map((d) => (
                <details key={d.id} data-testid={`ai-draft-history-${d.id}`} className="border border-[#E2E8F0] rounded-md p-3">
                  <summary className="text-sm font-medium text-[#1C1C1E] cursor-pointer flex items-center justify-between">
                    <span className="capitalize">{d.draft_type.replace(/_/g, " ")} · {new Date(d.created_at).toLocaleDateString()}</span>
                  </summary>
                  <p className="text-sm text-[#4B5563] whitespace-pre-wrap mt-2">{d.content}</p>
                </details>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </SheetContent>
      <InvoiceFormDialog open={invoiceDialogOpen} onOpenChange={setInvoiceDialogOpen} clientId={clientId} onCreated={load} />
      <MeetingFormDialog open={meetingDialogOpen} onOpenChange={setMeetingDialogOpen} defaultClientId={clientId} onCreated={load} />
    </Sheet>
  );
}

function Field({ label, value, className }) {
  return (
    <div className={className}>
      <p className="text-xs text-[#4B5563] uppercase tracking-wide mb-0.5">{label}</p>
      <p className="text-[#1C1C1E] font-medium">{value || "—"}</p>
    </div>
  );
}

function ClientFileSection({ title, icon: Icon, files, emptyText, onDelete, onReplace }) {
  return (
    <div className="border border-[#E2E8F0] bg-white rounded-md flex flex-col">
      <div className="border-b border-[#E2E8F0] p-3 bg-[#F8F9FA] flex items-center gap-2 rounded-t-md">
        <Icon className="h-4 w-4 text-[#00823B]" />
        <p className="text-sm font-semibold text-[#1C1C1E]">{title}</p>
        <span className="text-xs text-[#4B5563] ml-auto">{files.length}</span>
      </div>
      {files.length === 0 ? (
        <div className="p-6 flex flex-col items-center justify-center text-center">
          <FileX2 className="h-5 w-5 text-[#4B5563]/40 mb-2" />
          <p className="text-xs text-[#4B5563]">{emptyText}</p>
        </div>
      ) : (
        <div className="divide-y divide-[#E2E8F0]">
          {files.map((f) => (
            <div key={f.id} data-testid={`client-file-row-${f.id}`} className="flex items-center justify-between p-3">
              <p className="text-sm text-[#1C1C1E] truncate min-w-0">{f.original_filename}</p>
              <div className="flex items-center gap-2.5 shrink-0 ml-2">
                <a href={fileUrl(f.file_path)} target="_blank" rel="noreferrer" data-testid={`client-file-view-${f.id}`} className="text-[#4B5563]" title="View">
                  <Eye className="h-4 w-4" />
                </a>
                <a href={downloadUrl(f.file_path, f.original_filename)} data-testid={`client-file-download-${f.id}`} className="text-[#00823B]" title="Download">
                  <Download className="h-4 w-4" />
                </a>
                <label className="cursor-pointer text-[#4B5563] hover:text-[#1C1C1E]" data-testid={`client-file-replace-${f.id}`} title="Replace file">
                  <Pencil className="h-4 w-4" />
                  <input type="file" className="hidden" onChange={(e) => e.target.files[0] && onReplace(f.id, e.target.files[0])} />
                </label>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <button data-testid={`client-file-delete-${f.id}`} className="text-red-600" title="Delete">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Delete "{f.original_filename}"?</AlertDialogTitle>
                      <AlertDialogDescription>This action cannot be undone.</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction onClick={() => onDelete(f.id)} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
